import { useState, useEffect, useCallback, useRef } from 'react';

// Cache duration in milliseconds (1 minute)
const CACHE_DURATION = 60 * 1000;

export const useBalanceData = () => {
  const [balances, setBalances] = useState({
    onChain: { balance: 0, valueUSD: 0 },
    mercury: { usdBalance: 0 },
    strike: { btcBalance: 0, usdBalance: 0 },
    lightning: { 
      balance: 0, 
      valueUSD: 0, 
      channelCount: 0,
      channelBalance: 0,
      onChainBalance: 0
    },
  });
  
  const [bitcoinPrice, setBitcoinPrice] = useState(null);
  const [isLoading, setIsLoading] = useState({
    onChain: false,
    mercury: false,
    strike: false,
    lightning: false,
    bitcoinPrice: false,
  });
  const [lastUpdated, setLastUpdated] = useState({
    onChain: null,
    mercury: null,
    strike: null,
    lightning: null,
    bitcoinPrice: null,
  });
  const [errors, setErrors] = useState({
    onChain: null,
    mercury: null,
    strike: null,
    lightning: null,
    bitcoinPrice: null,
  });

  // Fetch Bitcoin price independently
  const fetchBitcoinPrice = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    if (!forceRefresh && lastUpdated.bitcoinPrice && (now - lastUpdated.bitcoinPrice < CACHE_DURATION)) {
      return;
    }

    setIsLoading(prev => ({ ...prev, bitcoinPrice: true }));
    setErrors(prev => ({ ...prev, bitcoinPrice: null }));

    try {
      const baseUrl = window.location.origin;
      const response = await fetch(`${baseUrl}/api/bitcoin-price`);
      if (!response.ok) {
        throw new Error('Failed to fetch Bitcoin price from API');
      }
      const { price } = await response.json();
      if (!price) {
        throw new Error('Invalid price data from API');
      }
      setBitcoinPrice(price);
      setLastUpdated(prev => ({ ...prev, bitcoinPrice: Date.now() }));
    } catch (err) {
      setErrors(prev => ({ ...prev, bitcoinPrice: err.message }));
      console.error('Bitcoin price fetch error:', err);
    } finally {
      setIsLoading(prev => ({ ...prev, bitcoinPrice: false }));
    }
  }, [lastUpdated.bitcoinPrice]);

  // Fetch On-Chain balance independently
  const fetchOnChainBalance = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    if (!forceRefresh && lastUpdated.onChain && (now - lastUpdated.onChain < CACHE_DURATION)) {
      return;
    }

    setIsLoading(prev => ({ ...prev, onChain: true }));
    setErrors(prev => ({ ...prev, onChain: null }));

    try {
      const baseUrl = window.location.origin;
      const price = bitcoinPrice || await fetch(`${baseUrl}/api/bitcoin-price`).then(r => r.json()).then(d => d.price);
      
      if (!price) {
        throw new Error('Bitcoin price not available');
      }

      const response = await fetch(`${baseUrl}/api/onchain-balance/${price}`);
      if (!response.ok) {
        throw new Error('Failed to fetch On-Chain balance');
      }

      const data = await response.json();
      setBalances(prev => ({ ...prev, onChain: data }));
      setLastUpdated(prev => ({ ...prev, onChain: Date.now() }));
    } catch (err) {
      setErrors(prev => ({ ...prev, onChain: err.message }));
      console.error('On-Chain balance fetch error:', err);
    } finally {
      setIsLoading(prev => ({ ...prev, onChain: false }));
    }
  }, [bitcoinPrice, lastUpdated.onChain]);

  // Fetch Mercury balance independently
  const fetchMercuryBalance = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    if (!forceRefresh && lastUpdated.mercury && (now - lastUpdated.mercury < CACHE_DURATION)) {
      return;
    }

    setIsLoading(prev => ({ ...prev, mercury: true }));
    setErrors(prev => ({ ...prev, mercury: null }));

    try {
      const baseUrl = window.location.origin;
      const response = await fetch(`${baseUrl}/api/mercury-balance`);
      if (!response.ok) {
        throw new Error('Failed to fetch Mercury balance');
      }

      const data = await response.json();
      setBalances(prev => ({ ...prev, mercury: data }));
      setLastUpdated(prev => ({ ...prev, mercury: Date.now() }));
    } catch (err) {
      setErrors(prev => ({ ...prev, mercury: err.message }));
      console.error('Mercury balance fetch error:', err);
    } finally {
      setIsLoading(prev => ({ ...prev, mercury: false }));
    }
  }, [lastUpdated.mercury]);

  // Fetch Strike balance independently
  const fetchStrikeBalance = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    if (!forceRefresh && lastUpdated.strike && (now - lastUpdated.strike < CACHE_DURATION)) {
      return;
    }

    setIsLoading(prev => ({ ...prev, strike: true }));
    setErrors(prev => ({ ...prev, strike: null }));

    try {
      const baseUrl = window.location.origin;
      const price = bitcoinPrice || await fetch(`${baseUrl}/api/bitcoin-price`).then(r => r.json()).then(d => d.price);
      
      if (!price) {
        throw new Error('Bitcoin price not available');
      }

      const response = await fetch(`${baseUrl}/api/strike-balance/${price}`);
      if (!response.ok) {
        throw new Error('Failed to fetch Strike balance');
      }

      const data = await response.json();
      setBalances(prev => ({ ...prev, strike: data }));
      setLastUpdated(prev => ({ ...prev, strike: Date.now() }));
    } catch (err) {
      setErrors(prev => ({ ...prev, strike: err.message }));
      console.error('Strike balance fetch error:', err);
    } finally {
      setIsLoading(prev => ({ ...prev, strike: false }));
    }
  }, [bitcoinPrice, lastUpdated.strike]);

  // Fetch Lightning balance independently
  const fetchLightningBalance = useCallback(async (forceRefresh = false) => {
    const now = Date.now();
    if (!forceRefresh && lastUpdated.lightning && (now - lastUpdated.lightning < CACHE_DURATION)) {
      return;
    }

    setIsLoading(prev => ({ ...prev, lightning: true }));
    setErrors(prev => ({ ...prev, lightning: null }));

    try {
      const baseUrl = window.location.origin;
      const price = bitcoinPrice || await fetch(`${baseUrl}/api/bitcoin-price`).then(r => r.json()).then(d => d.price);
      
      if (!price) {
        throw new Error('Bitcoin price not available');
      }

      const response = await fetch(`${baseUrl}/api/lightning-balance/${price}`);
      if (!response.ok) {
        throw new Error('Failed to fetch Lightning balance');
      }

      const data = await response.json();
      setBalances(prev => ({ ...prev, lightning: data }));
      setLastUpdated(prev => ({ ...prev, lightning: Date.now() }));
    } catch (err) {
      setErrors(prev => ({ ...prev, lightning: err.message }));
      console.error('Lightning balance fetch error:', err);
    } finally {
      setIsLoading(prev => ({ ...prev, lightning: false }));
    }
  }, [bitcoinPrice, lastUpdated.lightning]);

  // Refresh all data (but independently)
  const refreshAll = useCallback(async (forceRefresh = false) => {
    // Fetch Bitcoin price first, then fetch all balances in parallel
    await fetchBitcoinPrice(forceRefresh);
    
    // Fetch all balances independently (they will use cached price if available)
    Promise.all([
      fetchOnChainBalance(forceRefresh),
      fetchMercuryBalance(forceRefresh),
      fetchStrikeBalance(forceRefresh),
      fetchLightningBalance(forceRefresh),
    ]).catch(err => {
      console.error('Error refreshing balances:', err);
    });
  }, [fetchBitcoinPrice, fetchOnChainBalance, fetchMercuryBalance, fetchStrikeBalance, fetchLightningBalance]);

  // Store latest refreshAll in a ref for interval
  const refreshAllRef = useRef(refreshAll);
  useEffect(() => {
    refreshAllRef.current = refreshAll;
  }, [refreshAll]);

  // Refresh a specific section
  const refreshSection = useCallback(async (section, forceRefresh = true) => {
    switch (section) {
      case 'onChain':
        await fetchOnChainBalance(forceRefresh);
        break;
      case 'mercury':
        await fetchMercuryBalance(forceRefresh);
        break;
      case 'strike':
        await fetchStrikeBalance(forceRefresh);
        break;
      case 'lightning':
        await fetchLightningBalance(forceRefresh);
        break;
      case 'bitcoinPrice':
        await fetchBitcoinPrice(forceRefresh);
        break;
      default:
        console.warn(`Unknown section: ${section}`);
    }
  }, [fetchOnChainBalance, fetchMercuryBalance, fetchStrikeBalance, fetchLightningBalance, fetchBitcoinPrice]);

  // Initial load - fetch all independently
  useEffect(() => {
    refreshAllRef.current();
    
    // Set up refresh interval (5 minutes)
    const refreshInterval = 5 * 60 * 1000;
    const interval = setInterval(() => refreshAllRef.current(), refreshInterval);
    
    return () => clearInterval(interval);
  }, []); // Only run on mount

  // Get overall loading state (true if any section is loading)
  const isAnyLoading = Object.values(isLoading).some(loading => loading);
  
  // Get most recent update time
  const mostRecentUpdate = Math.max(...Object.values(lastUpdated).filter(Boolean), 0) || null;

  return {
    balances,
    bitcoinPrice,
    isLoading,
    isAnyLoading,
    lastUpdated,
    mostRecentUpdate,
    errors,
    refreshAll: () => refreshAll(true),
    refreshSection,
  };
}; 